import { IController } from 'angular';
import * as _ from 'lodash';

import { RegentsSubject } from '../../../../../ng2/shared/constants/regents.constant';

interface IAllPrepsFor {
  nvAssigned: string;
  optional1: string;
  optional2: string;
}

interface IAllPrepsForHash {
  [courseId: string]: IAllPrepsFor;
}

export class MasterProgramTableCtrl implements IController {
  static $inject = ['$scope', '$filter'];

  // Bindings
  private orderBy;
  private initialSortFields;
  private reverseSort;
  private _coursesNotFiltered;
  private allCoursesSelected;
  private prepCourses;
  private sortField;
  private masterProgram;
  private subjectAreas;
  private courseFilter;
  private teachers;
  private selectedCourses;

  // ctrl props
  public allPrepsForHash;
  private SORT_PREPS_FOR_HASH_FIELDS = ['nvAssigned', 'optional1', 'optional2'];

  constructor(private $scope, private $filter) {}

  $onInit() {
    this.orderBy = this.$filter('orderBy');

    // initial sort conditions
    this.initialSortFields = ['subjectArea', 'courseCode', 'teacher'];
    this.reverseSort = false;
    this.masterProgram = this.orderBy(this.masterProgram, this.initialSortFields, this.reverseSort);
    this.$scope.courseFilter = this.courseFilter;

    // initial state for filtered courses
    this._coursesNotFiltered = [];
    this.allCoursesSelected = false;

    _.each(this.masterProgram, (course: any) => {
      let pickedObj;
      // updates this.prepCourses which is used to update currentSchool.userEnteredPrepCourses
      if (course.prepCourses && !_.isEmpty(course.prepCourses)) {
        pickedObj = _.pick(course, 'courseId', 'prepCourses');
        this.prepCourses[pickedObj.courseId] = pickedObj.prepCourses;
        return this.prepCourses;
      }

      // makes select sortable
      let key = 'prepCourses';
      course[key] = _.find(this.prepCourses, (prepCourse, k) => {
        if (course.courseId === k) {
          return prepCourse;
        }
      });
    });

    this.allPrepsForHash = this.getAllPrepsForHash(this.masterProgram);

    this.$scope.$watch(
      'courseFilter',
      () => {
        this._filterCourses();
      },
      true,
    );

    this.$scope.$watch(
      'selectedCourses',
      () => {
        this._filterCourses();
      },
      true,
    );

    this.$scope.filterCourseCodeWithSection = function(query: string) {
      const searchTextQuery = query.toLowerCase();

      return course => {
        let courseCodeWithSection;

        if (course.courseCodeWithSection) {
          courseCodeWithSection = course.courseCodeWithSection;
        } else {
          courseCodeWithSection = `${course.courseCode}-${course.section}`;
        }

        return _.includes(course.courseCodeWithSection.toLowerCase(), searchTextQuery);
      };
    };
  }

  $onChanges(changesObj) {
    const { prepCourses } = changesObj;

    if (prepCourses) {
      const firstChange = prepCourses.isFirstChange();

      // `$onChanges` gets called at runtime.
      // `isFirstChange` will return `false` any time `$onChanges` is called after the initial
      // invocation that happens at runtime.
      // We do this check to avoid the logic below running at runtime. -CM
      if (!firstChange) this.allPrepsForHash = this.getAllPrepsForHash(this.masterProgram);
    }
  }

  // Methods

  // icon state
  getSortIcon = field => {
    return _.includes(this.initialSortFields, field);
  };

  activeSort = field => {
    if (field === this.sortField || _.includes(this.initialSortFields, field)) {
      return true;
    }
  };

  // sort on click
  sortBy(field: string) {
    // if there is a field param and it equals the current sortField, then switch the sort order
    this.reverseSort = field !== null && this.sortField === field ? !this.reverseSort : false;
    this.initialSortFields = []; // no longer need to retain initial sort that is applied on load
    this.sortField = field;
    this.getSortIcon(field);

    if (_.includes(this.SORT_PREPS_FOR_HASH_FIELDS, field)) {
      // sorts by props NOT directly on masterProgram -CM
      this.masterProgram.sort(this.sortByAllPrepsForHash.bind(this, field));
    } else {
      // sorts by props directly on masterProgram -CM
      this.masterProgram = this.orderBy(this.masterProgram, this.sortField, this.reverseSort);
    }
  }

  sortByAllPrepsForHash(field, course1, course2) {
    const a = (this.allPrepsForHash[course1.courseId][field] || '').toLowerCase();
    const b = (this.allPrepsForHash[course2.courseId][field] || '').toLowerCase();

    if (a > b) {
      return this.reverseSort ? -1 : 1;
    } else if (a < b) {
      return this.reverseSort ? 1 : -1;
    } else {
      return 0;
    }
  }

  getSubjectAreaHumanName = subjectArea => {
    return this.subjectAreas[subjectArea];
  };

  getRegentsHumanName = function(classKey) {
    let regentsObj = _.find(RegentsSubject, { key: classKey });
    return regentsObj ? regentsObj.humanName : classKey;
  };

  getAllPrepsForHash(masterProgram): IAllPrepsForHash {
    const allPrepsForHash: IAllPrepsForHash = _.reduce(
      masterProgram,
      (acc: IAllPrepsForHash, course: any) => {
        const { courseId } = course;
        const courseIdExistsInHash = acc[courseId];

        if (!courseIdExistsInHash) acc[courseId] = this.getAllPrepsFor(course);

        return acc;
      },
      {},
    );

    return allPrepsForHash;
  }

  getAllPrepsFor(course): IAllPrepsFor {
    const { coursePrepsFor, prepCourses } = course;
    const allPrepsFor = {
      nvAssigned: null,
      optional1: null,
      optional2: null,
    };

    if (coursePrepsFor) allPrepsFor.nvAssigned = this.getRegentsHumanName(coursePrepsFor);

    if (prepCourses) {
      const { prep1, prep2 } = prepCourses;

      if (prep1) allPrepsFor.optional1 = this.getRegentsHumanName(prep1);
      if (prep2) allPrepsFor.optional2 = this.getRegentsHumanName(prep2);
    }

    return allPrepsFor;
  }

  toggleAllCourses = () => {
    this.allCoursesSelected = !this.allCoursesSelected;

    _.each(this._coursesNotFiltered, (course: any) => {
      course.isChecked = this.allCoursesSelected;
    });

    return this.setSelectedCourses();
  };

  toggleAllIsIndeterminate = () => {
    const { everyCourseIsChecked, everyCourseIsUnchecked } = this._areAllCoursesCheckedOrUnchecked();

    if (everyCourseIsChecked) {
      this.allCoursesSelected = true;
    } else {
      this.allCoursesSelected = false;
    }

    return !everyCourseIsChecked && !everyCourseIsUnchecked;
  };

  setSelectedCourses = () => {
    this.selectedCourses = _.filter(this._coursesNotFiltered, (course: any) => {
      return course.isChecked;
    });

    this.selectedCourses.length
      ? this._isAtLeastOneCourseCheckedEmitter(true)
      : this._isAtLeastOneCourseCheckedEmitter(false);
  };

  _filterCourses = () => {
    this._coursesNotFiltered = [];
    _.each(this.masterProgram, (course: any) => {
      course.isFiltered =
        this._isCourseFilteredBySubjectArea(course) ||
        this._isCourseFilteredByCodeWithSection(course) ||
        this._isCourseFilteredByTeachers(course) ||
        this._isCourseFilteredByIsRegentsCulm(course) ||
        this._isCourseFilteredByPrepsFor(course);

      if (!course.isFiltered) this._coursesNotFiltered.push(course);

      return course;
    });

    this.setSelectedCourses();
  };

  _isCourseFilteredBySubjectArea = course => {
    const subjectAreaFilter = this.$scope.courseFilter.subjectArea;

    return !_.isNull(subjectAreaFilter) && this.getSubjectAreaHumanName(course.subjectArea) !== subjectAreaFilter
      ? true
      : false;
  };

  _isCourseFilteredByCodeWithSection = course => {
    const codeWithSectionFilter = this.$scope.courseFilter.courseCodeWithSection;

    if (codeWithSectionFilter) {
      const filterFunc = this.$scope.filterCourseCodeWithSection(codeWithSectionFilter);
      const filtered = [course].filter(filterFunc);

      return filtered.length ? false : true;
    }

    return false;
  };

  _isCourseFilteredByTeachers = course => {
    const teachersFilter = this.$scope.courseFilter.teachers;

    return !_.isNull(teachersFilter) && this.teachers(course.teachers) !== teachersFilter ? true : false;
  };

  _isCourseFilteredByIsRegentsCulm = course => {
    const { isRegentsCulminating } = course;
    const isRegentsCulminatingFilterMapping = {
      Yes: true,
      No: false,
    };
    const { isRegentsCulminating: isRegentsCulminatingFilter } = this.$scope.courseFilter;

    return !_.isNull(isRegentsCulminatingFilter) &&
      isRegentsCulminating !== isRegentsCulminatingFilterMapping[isRegentsCulminatingFilter]
      ? true
      : false;
  };

  _isCourseFilteredByPrepsFor = course => {
    const prepsForFilter = this.$scope.courseFilter.prepsFor;
    const coursePrepsForValues = [];

    if (!_.isNull(prepsForFilter)) {
      coursePrepsForValues.push(this.getRegentsHumanName(course.coursePrepsFor));

      if (course.prepCourses) {
        coursePrepsForValues.push(this.getRegentsHumanName(course.prepCourses.prep1));
        coursePrepsForValues.push(this.getRegentsHumanName(course.prepCourses.prep2));
      }

      return !_.includes(coursePrepsForValues, prepsForFilter);
    }

    return false;
  };

  _isAtLeastOneCourseCheckedEmitter = trueOrFalse => {
    return this.$scope.$emit('atLeastOneCourseIsChecked', trueOrFalse);
  };

  _areAllCoursesCheckedOrUnchecked = () => {
    const everyCourseIsChecked =
      this.selectedCourses.length && this.selectedCourses.length === this._coursesNotFiltered.length;
    const everyCourseIsUnchecked = this.selectedCourses.length === 0;

    return { everyCourseIsChecked, everyCourseIsUnchecked };
  };
}

angular.module('nv.SchoolMetricsModule').controller('MasterProgramTableCtrl', MasterProgramTableCtrl);
